import React from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { GET_FLEET_STATS } from '../graphql/queries';
import { DataTable } from '../components/DataTable';

const PIE_COLORS = ['#00b894', '#0984e3', '#fdcb6e', '#d63031'];

function StatCard({ label, value, color }) {
  return (
    <div className="stat-card" style={{ borderLeft: `4px solid ${color}` }}>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

const instanceImageColumns = [
  { accessorKey: 'imageName', header: 'Image', cell: ({ row }) => row.original.imageName || row.original.amiId },
  {
    accessorKey: 'amiId',
    header: 'AMI ID',
    cell: ({ getValue }) => (
      <Link to={`/fleet/instances?amiIds=${getValue()}`} style={{ color: '#0984e3', textDecoration: 'none' }}>
        <code style={{ fontSize: 12 }}>{getValue()}</code>
      </Link>
    ),
  },
  { accessorKey: 'count', header: 'Instances', cell: ({ getValue }) => (
    <span style={{ fontWeight: 600, fontSize: 16 }}>{getValue()}</span>
  )},
];

export default function Fleet() {
  const { data, loading, error } = useQuery(GET_FLEET_STATS, { pollInterval: 60000 });

  if (loading) return <div className="loading">Discovering deployed instances...</div>;
  if (error) return <div className="error">Failed to load fleet data: {error.message}</div>;

  const stats = data?.getFleetStats;
  if (!stats) return <div className="error">No fleet data available</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 className="page-title" style={{ margin: 0 }}>Fleet Dashboard</h2>
        <Link
          to="/fleet/instances"
          style={{
            padding: '8px 16px',
            background: '#0984e3',
            color: '#fff',
            borderRadius: 6,
            textDecoration: 'none',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          Lookup Instances by AMI
        </Link>
      </div>

      <div className="stats-grid">
        <StatCard label="Total Instances" value={stats.totalInstances} color="#0984e3" />
        <StatCard label="Unique AMIs" value={stats.byImage?.length || 0} color="#6c5ce7" />
        <StatCard label="Drift Count" value={stats.driftCount} color={stats.driftCount > 0 ? '#e17055' : '#00b894'} />
        <StatCard
          label="Stale (90d+)"
          value={stats.ageDistribution?.find(b => b.label === '90d+')?.count || 0}
          color="#d63031"
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16 }}>
        {stats.byImage?.length > 0 && (
          <div className="card">
            <h3 style={{ marginBottom: 16 }}>Instances per Image</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={stats.byImage}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="imageName" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#0984e3" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {stats.ageDistribution?.length > 0 && (
          <div className="card">
            <h3 style={{ marginBottom: 16 }}>Instance Age Distribution</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={stats.ageDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={4}
                  dataKey="count"
                  label={({ label, count }) => `${label}: ${count}`}
                >
                  {stats.ageDistribution.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {stats.byImage?.length > 0 && (
        <div className="card">
          <h3 style={{ marginBottom: 12 }}>Images in Use</h3>
          <DataTable columns={instanceImageColumns} data={stats.byImage} pageSize={10} />
        </div>
      )}

      {stats.driftCount > 0 && (
        <div className="card" style={{ borderLeft: '4px solid #e17055' }}>
          <h3 style={{ color: '#e17055' }}>Drift Detected</h3>
          <p style={{ color: '#636e72' }}>
            {stats.driftCount} instance{stats.driftCount !== 1 ? 's are' : ' is'} running non-latest AMIs.
            Consider updating to the latest channel AMI.
          </p>
        </div>
      )}
    </div>
  );
}
